import { registerLocaleData } from '@angular/common';
import { HTTP_INTERCEPTORS } from '@angular/common/http';
import localeEs from '@angular/common/locales/es';
import { ApplicationConfig, EnvironmentProviders, importProvidersFrom, makeEnvironmentProviders } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { provideRouter } from '@angular/router';
import { MSAL_GUARD_CONFIG, MSAL_INSTANCE, MSAL_INTERCEPTOR_CONFIG, MsalBroadcastService, MsalGuard, MsalGuardConfiguration, MsalInterceptor, MsalInterceptorConfiguration, MsalService } from '@azure/msal-angular';
import { BrowserCacheLocation, Configuration, InteractionType, LogLevel, PublicClientApplication } from '@azure/msal-browser';
import { IHowdenEnvironment, IPackageJsonWrapper, isHowdenGroupB2CEnvironment, isProductionEnvironment, provideHowdenCore } from '@howdeniberia/core-front';
import packageJson from '../../package.json';
import { ApiConfigurations } from '../environments/api-configurations';
import { environment } from '../environments/environment';
import { AppRoutes } from './app-routing';
import { provideCoreServices } from './core/core.provider';

registerLocaleData(localeEs, 'es');

const isIE = window.navigator.userAgent.indexOf('MSIE ') > -1 || window.navigator.userAgent.indexOf('Trident/') > -1;

function loggerCallback(logLevel: LogLevel, message: string) {
  if (isProductionEnvironment(environment.zone)) {
    return;
  }

  if (logLevel === LogLevel.Error) {
    console.error(message);
  } else if (logLevel === LogLevel.Info) {
    // eslint-disable-next-line no-console
    console.info(message);
  } else if (logLevel === LogLevel.Trace) {
    // eslint-disable-next-line no-console
    console.trace(message);
  } else if (logLevel === LogLevel.Warning) {
    console.warn(message);
  } else {
    // eslint-disable-next-line no-console
    console.log(message);
  }
}

function configureAzureB2CInstance(): Configuration {
  const configuration = <Configuration>{
    auth: {
      clientId: environment.openid!.msalConfig.auth.clientId,
      authority: environment.openid!.policies!.authorities.signUpSignIn.authority,
      redirectUri: environment.openid!.msalConfig.auth.redirectUri(),
      postLogoutRedirectUri: environment.openid!.msalConfig.auth.postLogoutRedirectUri(),
      knownAuthorities: [environment.openid!.policies!.authorityDomain]
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE  // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  };

  return configuration;
}

function configureMicrosoftEntraInstance(): Configuration {
  const configuration = <Configuration>{
    auth: {
      clientId: environment.openid!.msalConfig.auth.clientId,
      authority: environment.openid!.msalConfig.auth.authority,
      redirectUri: environment.openid!.msalConfig.auth.redirectUri(),
      postLogoutRedirectUri: environment.openid!.msalConfig.auth.postLogoutRedirectUri()
    },
    cache: {
      cacheLocation: BrowserCacheLocation.LocalStorage,
      storeAuthStateInCookie: isIE  // set to true for IE 11
    },
    system: {
      loggerOptions: {
        loggerCallback,
        logLevel: LogLevel.Info,
        piiLoggingEnabled: false
      }
    }
  };

  return configuration;
}

function msalInstanceFactory(): PublicClientApplication {
  const configuration = isHowdenGroupB2CEnvironment(environment.zone)
    ? configureAzureB2CInstance()
    : configureMicrosoftEntraInstance();

  return new PublicClientApplication(configuration);
}

function msalGuardConfigFactory(): MsalGuardConfiguration {
  const configuration = {
    interactionType: InteractionType.Redirect,
    authRequest: {
      scopes: [...environment.openid!.apiConfig.scopes]
    },
    loginFailedRoute: '/login-failed'
  } as MsalGuardConfiguration;

  return configuration;
}

function msalInterceptorConfigFactory(): MsalInterceptorConfiguration {
  const configuration = {
    interactionType: InteractionType.Redirect,
    protectedResourceMap: environment.openid!.protectedResources!
  } as MsalInterceptorConfiguration;

  return configuration;
}

function provideMsal(): EnvironmentProviders {
  return makeEnvironmentProviders([
    { provide: MsalService, useClass: MsalService },
    { provide: MsalGuard, useClass: MsalGuard },
    { provide: MsalBroadcastService, useClass: MsalBroadcastService },
    { provide: MSAL_INSTANCE, useFactory: msalInstanceFactory },
    { provide: MSAL_GUARD_CONFIG, useFactory: msalGuardConfigFactory },
    { provide: MSAL_INTERCEPTOR_CONFIG, useFactory: msalInterceptorConfigFactory },
    { provide: HTTP_INTERCEPTORS, useClass: MsalInterceptor, multi: true }
  ]);
}

export const appConfig: ApplicationConfig = {
  providers: [
    importProvidersFrom(BrowserModule),
    provideCoreServices(),
    provideRouter(AppRoutes),
    provideHowdenCore({
      environment: environment as IHowdenEnvironment,
      packageJson: packageJson as unknown as IPackageJsonWrapper,
      apiConfigurations: ApiConfigurations
    }),
    provideMsal()
  ]
};
